import { AppLayout } from 'modules/app/components/templates/app-layout/AppLayout';
import { createBrowserRouter } from 'react-router-dom';
import { NotFound } from 'modules/app/components/pages/not-found/NotFound';
import { RouteBoundary } from 'modules/app/components/molecules/route-boundary/RouteBoundary';
import { routes } from './route-paths';
import { ConfiguredRedirectUri } from 'auth';
import { AuthenticatedRoute } from 'components/molecules/authenticated-route/AuthenticatedRoute';
import AuthenticatedLayout from 'modules/app/components/templates/app-layout/AuthenticatedLayout';
import { ImsLoginInitiator } from 'components/molecules/ims-login-initiator/ImsLoginInitiator';
import { FullScreenLayout } from 'modules/app/components/templates/app-layout/FullScreenLayout';

export const createRouter = (basename: string) =>
  createBrowserRouter(
    [
      {
        path: routes.auth,
        element: <ConfiguredRedirectUri />
      },
      {
        path: routes.ims,
        element: <ImsLoginInitiator />
      },
      {
        element: <AuthenticatedLayout />,
        children: [
          {
            element: <FullScreenLayout />,
            children: [
              {
                path: routes.legal,
                lazy: async () => {
                  const { LegalPage: Component } = await import('modules/legal/Legal');
                  return { Component };
                }
              }
            ]
          },
          {
            element: <AppLayout />,
            children: [
              {
                index: true,
                // Defaulted to authenticated route to redirect to ims for now
                element: <AuthenticatedRoute />
              },
              {
                path: routes.dashboard,
                lazy: async () => {
                  const { Dashboard } = await import('modules/dashboard/Dashboard');
                  return {
                    Component: () => (
                      <RouteBoundary>
                        <Dashboard />
                      </RouteBoundary>
                    )
                  };
                },
                handle: {
                  breadcrumb: { text: 'Dashboard' },
                  navigationPath: routes.dashboard
                }
              },
              {
                path: routes.investments,
                lazy: async () => {
                  const { ClientInvestments } = await import('modules/investments/Investments');
                  return {
                    Component: () => (
                      <RouteBoundary>
                        <ClientInvestments />
                      </RouteBoundary>
                    )
                  };
                },
                handle: {
                  breadcrumb: { text: 'Investments' },
                  navigationPath: routes.investments
                }
              },
              {
                path: routes.details,
                lazy: async () => {
                  const { ClientDetails } = await import('modules/details/Details');
                  return {
                    Component: () => (
                      <RouteBoundary>
                        <ClientDetails />
                      </RouteBoundary>
                    )
                  };
                },
                handle: {
                  breadcrumb: { text: 'Personal Details' },
                  navigationPath: routes.details
                }
              },
              {
                path: routes.transact,
                lazy: async () => {
                  const { Transact } = await import('modules/transact/Transact');
                  return {
                    Component: () => (
                      <RouteBoundary>
                        <Transact />
                      </RouteBoundary>
                    )
                  };
                },
                handle: {
                  breadcrumb: { text: 'Transact' },
                  navigationPath: routes.transact
                }
              },
              {
                path: routes.tracking,
                lazy: async () => {
                  const { Tracking } = await import('modules/tracking/Tracking');
                  return {
                    Component: () => (
                      <RouteBoundary>
                        <Tracking />
                      </RouteBoundary>
                    )
                  };
                },
                handle: {
                  breadcrumb: { text: 'Tracking' },
                  navigationPath: routes.tracking
                }
              },
              {
                path: routes.correspondence,
                lazy: async () => {
                  const { Correspondence } = await import('modules/correspondence/Correspondence');
                  return {
                    Component: () => (
                      <RouteBoundary>
                        <Correspondence />
                      </RouteBoundary>
                    )
                  };
                },
                handle: {
                  breadcrumb: { text: 'Correspondence' },
                  navigationPath: routes.correspondence
                }
              },
              {
                path: routes.otherApps,
                lazy: async () => {
                  const { OtherApps } = await import('modules/other-apps/components/OtherApps');
                  return {
                    Component: () => (
                      <RouteBoundary>
                        <OtherApps />
                      </RouteBoundary>
                    )
                  };
                },
                handle: {
                  breadcrumb: { text: 'Other apps' },
                  navigationPath: routes.otherApps
                }
              },
              {
                path: routes.settings,
                lazy: async () => {
                  const { Settings } = await import('modules/settings/Settings');
                  return {
                    Component: () => (
                      <RouteBoundary>
                        <Settings />
                      </RouteBoundary>
                    )
                  };
                },
                handle: {
                  navigationPath: routes.settings
                }
              },
              {
                path: routes.support,
                lazy: async () => {
                  const { SupportPage } = await import('modules/support/SupportPage');
                  return {
                    Component: () => (
                      <RouteBoundary>
                        <SupportPage />
                      </RouteBoundary>
                    )
                  };
                },
                handle: {
                  breadcrumb: { text: 'Support' },
                  navigationPath: routes.support
                }
              },
              {
                path: routes.comingSoon,
                lazy: async () => {
                  const { ComingSoon } = await import('modules/coming-soon/ComingSoon');
                  return {
                    Component: () => (
                      <RouteBoundary>
                        <ComingSoon />
                      </RouteBoundary>
                    )
                  };
                }
              },
              {
                path: '*',
                element: <NotFound />
              }
            ]
          }
        ]
      }
    ],
    { basename }
  );
